import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Droplets,
  ShieldCheck,
  Activity,
  Building2,
  Settings2,
  BarChart3,
  CheckCircle2,
} from "lucide-react";
import { Link } from "react-router-dom";
import chlorination from "../images/chlorination.jpeg";

function CounterCard({ value, suffix, label }) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!hasStarted) return;
    let start = 0;
    const duration = 1400;
    const stepTime = Math.max(Math.floor(duration / value), 20);

    const timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= value) clearInterval(timer);
    }, stepTime);

    return () => clearInterval(timer);
  }, [value, hasStarted]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      onViewportEnter={() => setHasStarted(true)}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5 }}
      className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl"
    >
      <div className="text-2xl font-bold text-cyan-400">
        {count}{suffix}
      </div>
      <div className="mt-1 text-sm text-slate-400">{label}</div>
    </motion.div>
  );
}

export default function HomePage() {
  const [activeSlide, setActiveSlide] = useState(0);

  const features = useMemo(
    () => [
      {
        title: "Advanced Purification",
        desc: "Multi-stage treatment with filtration, RO, UV and disinfection for safe and premium water quality.",
        icon: Droplets,
      },
      {
        title: "IoT Monitoring",
        desc: "Track TDS, pH, pressure, flow, tank levels and system health in real time.",
        icon: Activity,
      },
      {
        title: "Built for Real Estate",
        desc: "Designed for societies, towers, villas, mansions, gated communities and luxury developments.",
        icon: Building2,
      },
      {
        title: "Health & Safety",
        desc: "Reduce bacterial and contamination risks while improving confidence in residential water quality.",
        icon: ShieldCheck,
      },
      {
        title: "Low Maintenance Design",
        desc: "Engineered for reliable long-term performance with service-friendly maintenance architecture.",
        icon: Settings2,
      },
      {
        title: "Custom Capacity",
        desc: "Every plant is sized according to occupancy, source water condition and daily demand.",
        icon: BarChart3,
      },
    ],
    []
  );

  const stats = [
    { value: 24, suffix: "/7", label: "Monitoring" },
    { value: 99, suffix: "%", label: "Reliability Target" },
    { value: 100, suffix: "+", label: "Design Inputs" },
    { value: 12, suffix: "+", label: "Use Cases" },
  ];

  const plantImages = [
    { title: "Integrated Filtration Plant", src: chlorination },
    {
      title: "Industrial Water Treatment Unit",
      src: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1400&q=80",
    },
    {
      title: "Treatment Infrastructure",
      src: "https://images.unsplash.com/photo-1513828583688-c52646db42da?auto=format&fit=crop&w=1400&q=80",
    },
    {
      title: "Premium Residential Utility Integration",
      src: "https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=1400&q=80",
    },
    {
      title: "Utility Water Network",
      src: "https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?auto=format&fit=crop&w=1400&q=80",
    },
    {
      title: "Township Water Infrastructure",
      src: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1400&q=80",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % plantImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [plantImages.length]);

  const nextSlide = () =>
    setActiveSlide((prev) => (prev + 1) % plantImages.length);

  const prevSlide = () =>
    setActiveSlide((prev) => (prev - 1 + plantImages.length) % plantImages.length);

  const fadeUp = {
    hidden: { opacity: 0, y: 34 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  return (
    <>
      <section className="mx-auto max-w-7xl px-6 pb-20 pt-20 lg:pt-28">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            variants={fadeUp}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200">
              Arsenic removal, iron removal, RO, UV &amp; softener plants for residential infrastructure
            </div>

            <h2 className="text-4xl font-bold leading-tight md:text-6xl">
              Arsenic Removal, Iron Removal, RO &amp; UV Plant in{" "}
              <span className="text-cyan-400">Kolkata, Newtown &amp; Rajarhat</span>
            </h2>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
              Complete water treatment — arsenic removal plants, iron removal
              plants, RO plants, UV systems, softener plants and water filters
              designed for apartment complexes, high-rise buildings, luxury
              homes and premium residential developments across Kolkata,
              Newtown and Rajarhat.
            </p>

            <div className="mt-4 flex flex-wrap gap-3 text-sm text-slate-300">
              {[
                "Custom capacity planning",
                "IoT-enabled monitoring",
                "Premium residential integration",
              ].map((item) => (
                <div
                  key={item}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-2"
                >
                  <CheckCircle2 size={16} className="text-cyan-300" />
                  {item}
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="rounded-2xl bg-cyan-500 px-7 py-3.5 font-semibold text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:bg-cyan-400"
              >
                Get Proposal
              </Link>
              <Link
                to="/about"
                className="rounded-2xl border border-white/15 bg-white/5 px-7 py-3.5 font-semibold transition hover:bg-white/10"
              >
                Learn More
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative"
          >
            <div className="floating-card overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 p-3 shadow-2xl backdrop-blur-xl">
              <div className="relative h-[420px] overflow-hidden rounded-[1.5rem]">
                {plantImages.map((img, index) => (
                  <img
                    key={img.title}
                    src={img.src}
                    alt={img.title}
                    className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 ${
                      activeSlide === index ? "opacity-100 scale-100" : "opacity-0 scale-105"
                    }`}
                  />
                ))}

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/10 to-transparent" />

                <button
                  onClick={prevSlide}
                  className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-slate-900/60 backdrop-blur hover:bg-slate-900/80"
                >
                  <ChevronLeft size={20} />
                </button>

                <button
                  onClick={nextSlide}
                  className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-slate-900/60 backdrop-blur hover:bg-slate-900/80"
                >
                  <ChevronRight size={20} />
                </button>

                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-sm uppercase tracking-[0.25em] text-cyan-300">
                    Featured Plant
                  </p>
                  <h3 className="mt-3 text-2xl font-bold">
                    {plantImages[activeSlide].title}
                  </h3>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((item, index) => (
            <CounterCard
              key={index}
              value={item.value}
              suffix={item.suffix}
              label={item.label}
            />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-400">
            Core Solutions
          </p>
          <h3 className="mt-4 text-4xl font-bold md:text-5xl">
            Complete Water Treatment &amp; Filtration Infrastructure
          </h3>
          <p className="mt-5 text-lg leading-8 text-slate-400">
            Premium arsenic removal plants, iron removal plants, RO plants,
            UV systems, softener plants and water filters built for residential
            societies in Kolkata, Newtown and Rajarhat — designed for
            reliability, modern utility standards and stronger buyer confidence.
          </p>
        </div>

        <div className="grid gap-7 md:grid-cols-2 xl:grid-cols-3">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="rounded-3xl border border-white/10 bg-white/5 p-7 backdrop-blur-xl transition duration-300 hover:-translate-y-1 hover:border-cyan-400/30"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-500/10 text-cyan-300">
                  <Icon size={24} />
                </div>
                <h4 className="text-2xl font-semibold">{feature.title}</h4>
                <p className="mt-4 leading-7 text-slate-400">{feature.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>
    </>
  );
}